import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Users, ClipboardList } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api, { platformAPI, relationshipAPI, dealAPI } from '@/lib/api';
import HomeShell from '@/components/home/HomeShell';
import HomeTiles from '@/components/home/HomeTiles';
import HomeLatestDeals from '@/components/home/HomeLatestDeals';
import {
  HomeSearchBar, HomeSectionTitle, HomeStakeholderStrip, HomeStatsCard,
} from '@/components/home/HomeBlocks';

const LATEST_DEALS = 10;

function _rowsOf(res) {
  if (res.status !== 'fulfilled') return [];
  const data = res.value?.data;
  if (Array.isArray(data)) return data;
  return data?.items || [];
}

// Same order on every role's home: people circles, search, latest
// deals, my statistics, tiles. A manufacturer sells rather than buys,
// so its latest deals are the ones it created.
export default function ManufacturerHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [deals, setDeals] = useState([]);
  const [platform, setPlatform] = useState({});
  const [stats, setStats] = useState({ products: 0, distributors: 0, pendingRequests: 0 });
  const [searchQ, setSearchQ] = useState('');

  const loadData = useCallback(async () => {
    const [prodRes, dealRes, netRes, pendRes, platRes] = await Promise.allSettled([
      api.get(`/manufacturer/products/${user?.id}`, { params: { page: 1, page_size: 10 } }),
      dealAPI.getSellerDeals(user?.id),
      relationshipAPI.getNetwork(user?.id),
      relationshipAPI.getPending(user?.id),
      platformAPI.getStatistics(),
    ]);
    const newestFirst = [..._rowsOf(dealRes)].sort(
      (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
    );
    setDeals(newestFirst.slice(0, LATEST_DEALS));

    // Only invites someone sent to me need attention; ones I sent are
    // waiting on the other party.
    const received = _rowsOf(pendRes).filter(
      (r) => r.direction === 'received' || r.to_user_id === user?.id,
    );
    const prodData = prodRes.status === 'fulfilled' ? prodRes.value.data : null;
    setStats({
      products: prodData?.total ?? _rowsOf(prodRes).length,
      distributors: netRes.status === 'fulfilled'
        ? ((netRes.value.data || {}).downstream || []).length
        : 0,
      pendingRequests: received.length,
    });
    if (platRes.status === 'fulfilled') setPlatform(platRes.value.data || {});
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    navigate(q ? `/products?search=${encodeURIComponent(q)}` : '/products');
  };

  const statTiles = [
    { icon: Package, iconClass: 'text-farm-primary bg-farm-primary/10',
      value: stats.products, label: t('manufacturerHome.stats.products') },
    { icon: Users, iconClass: 'text-farm-success bg-farm-success/10',
      value: stats.distributors, label: t('manufacturerHome.stats.distributors') },
    { icon: ClipboardList, iconClass: 'text-farm-warning bg-farm-warning/10',
      value: stats.pendingRequests, label: t('manufacturerHome.stats.pendingRequests', { defaultValue: 'Pending requests' }) },
  ];

  return (
    <HomeShell>
      <HomeStakeholderStrip platform={platform} t={t} />
      <HomeSearchBar
        value={searchQ}
        onChange={setSearchQ}
        onSubmit={onSearchSubmit}
        testId="manufacturer-home-search-input"
      />
      <HomeLatestDeals deals={deals} />
      <HomeSectionTitle>{t('manufacturerHome.myStatistics')}</HomeSectionTitle>
      <HomeStatsCard tiles={statTiles} />
      <HomeTiles role="manufacturer" />
    </HomeShell>
  );
}
